import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import WhiteWarningIcon from '../../assets/white-warning.svg';
import {useLocation} from 'react-router-dom';
import useServiceWorker from './useServiceWorker';
import {GREY_30, WHITE_100} from '../../theme';

// Detects router changes and service-worker status to decide when to show toast.
export function useNewReleaseToast() {
  const {hasNewRelease, waitingWorker, reloadPage} = useServiceWorker();
  const [showToast, setShowToast] = useState(false);
  const location = useLocation();

  // On every navigation check for a newer SW and decide whether to show toast.
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg) {
          reg.update();
        }
      });
    }

    if (hasNewRelease && waitingWorker) {
      setShowToast(true);
    }
  }, [location, hasNewRelease, waitingWorker]);

  return {
    showToast,
    dismissToast: () => setShowToast(false),
    reloadPage,
  };
}

// A toast that appears when a new version of the app is available.
export function NewReleaseToast({show, dismissToast, reloadPage}) {
  const [open, setOpen] = useState(show);

  useEffect(() => {
    setOpen(show);
  }, [show]);

  const handleClose = (_, reason) => {
    // ignore clicks on the backdrop
    if (reason === 'clickaway') return;
    setOpen(false);
    dismissToast();
  };

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      message={
        <Stack direction="row" alignItems="center" gap={1.5} color={WHITE_100}>
          <img src={WhiteWarningIcon} alt="Warning" width={32} height={32} />
          <Stack>
            <Typography
              fontSize={14}
              fontWeight={600}
              lineHeight="130%"
              letterSpacing="-0.14px"
            >
              New version is available
            </Typography>
            <Typography
              fontSize={12}
              fontWeight={500}
              lineHeight="140%"
              letterSpacing="-0.12px"
            >
              Product is updated, please refresh.
            </Typography>
          </Stack>
        </Stack>
      }
      anchorOrigin={{vertical: 'top', horizontal: 'center'}}
      sx={{
        '&.MuiSnackbar-anchorOriginTopCenter': {
          top: '94px',
        },
        '& .MuiPaper-root': {
          backgroundColor: GREY_30,
          color: WHITE_100,
          boxShadow: '0 2px 12px 0 rgba(0, 0, 0, 0.12)',
          fontWeight: '500',
          fontSize: '14px',
          lineHeight: '100%',
          letterSpacing: '0%',
          px: 3,
          py: 2,
          borderRadius: 2,
          width: '570px',
          '.MuiSnackbarContent-message': {
            p: 0,
          },
          '.MuiSnackbarContent-action': {
            mr: 0,
          },
        },
      }}
      action={
        <Button
          variant="outlined"
          size="medium"
          sx={{
            color: WHITE_100,
            borderColor: WHITE_100,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '140%',
            letterSpacing: '-0.12px',
            px: 1.5,
            py: 1,
            borderRadius: 2,
            '&:hover': {
              backgroundColor: WHITE_100,
              color: GREY_30,
            },
          }}
          onClick={reloadPage}
        >
          Refresh now
        </Button>
      }
    />
  );
}

NewReleaseToast.propTypes = {
  show: PropTypes.bool.isRequired,
  reloadPage: PropTypes.func.isRequired,
  dismissToast: PropTypes.func,
};
